// ELK layered layout — FXC-8538 (Warp).
//
// Computes a hierarchical (layered) layout for the canonical graph using
// ELK — the same engine Thread-web's DAGView uses. Produces fixed node
// positions + routed edge polylines, so a DAG reads as proper top-down /
// left-right lineage instead of a force blob. Async (ELK runs off the
// render path); the renderer shows a placeholder until it resolves.

import ELK from "elkjs/lib/elk.bundled.js";

import type { GraphEdge, GraphNode } from "../core/types";

export interface ElkPositionedNode {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ElkRoutedEdge {
  source: string;
  target: string;
  predicate: string;
  points: { x: number; y: number }[];
}

export interface ElkLayout {
  width: number;
  height: number;
  nodes: ElkPositionedNode[];
  edges: ElkRoutedEdge[];
}

export type ElkDirection = "RIGHT" | "DOWN";

const elk = new ELK();

/** Estimate a node box width from its label. */
function nodeWidth(label: string): number {
  return Math.max(52, Math.min(240, label.length * 7 + 18));
}

const NODE_HEIGHT = 26;

/** Run ELK layered layout. Resolves to fixed positions + routed edges. */
export async function layoutWithElk(
  nodes: GraphNode[],
  edges: GraphEdge[],
  opts: { direction?: ElkDirection } = {},
): Promise<ElkLayout> {
  const direction = opts.direction ?? "RIGHT";
  const present = new Set(nodes.map((n) => n.id));

  const graph = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": direction,
      "elk.spacing.nodeNode": "26",
      "elk.layered.spacing.nodeNodeBetweenLayers": "64",
      "elk.layered.spacing.edgeNodeBetweenLayers": "24",
      "elk.edgeRouting": "POLYLINE",
    },
    children: nodes.map((n) => ({
      id: n.id,
      width: nodeWidth(n.label),
      height: NODE_HEIGHT,
    })),
    edges: edges
      .filter((e) => present.has(e.source) && present.has(e.target))
      .map((e, i) => ({ id: `e${i}`, sources: [e.source], targets: [e.target] })),
  };

  // elkjs types are loose around the result shape; treat defensively.
  const res = (await elk.layout(graph as never)) as {
    width?: number;
    height?: number;
    children?: { id: string; x?: number; y?: number; width?: number; height?: number }[];
    edges?: {
      sources: string[];
      targets: string[];
      sections?: {
        startPoint: { x: number; y: number };
        endPoint: { x: number; y: number };
        bendPoints?: { x: number; y: number }[];
      }[];
    }[];
  };

  const positioned: ElkPositionedNode[] = (res.children ?? []).map((c) => ({
    id: c.id,
    x: c.x ?? 0,
    y: c.y ?? 0,
    width: c.width ?? 52,
    height: c.height ?? NODE_HEIGHT,
  }));

  const predicateByPair = new Map<string, string>();
  for (const e of edges) predicateByPair.set(`${e.source} ${e.target}`, e.predicate);

  const routed: ElkRoutedEdge[] = (res.edges ?? []).map((e) => {
    const source = e.sources[0] ?? "";
    const target = e.targets[0] ?? "";
    const sec = e.sections?.[0];
    const points = sec
      ? [sec.startPoint, ...(sec.bendPoints ?? []), sec.endPoint]
      : [];
    return {
      source,
      target,
      predicate: predicateByPair.get(`${source} ${target}`) ?? "child_of",
      points,
    };
  });

  return {
    width: res.width ?? 100,
    height: res.height ?? 100,
    nodes: positioned,
    edges: routed,
  };
}
